import { StatusTag } from "@/views/components/StatusTag";
import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Form, Input, Select, Space, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { EmptyShipVo } from "@/entity/logistics/emptyShipInfo";
import type { Page } from "@zhihaoscm/entity";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { EMPTY_INFO_ORIGIN, EMPTY_SHIP_STATE, EXIST_DEVICE } from "@/meta/logistics/emptyShipInfo";
import { useUserStore } from "@/stores/react/authStore";
import { confirmDialog } from "@/utils/confirmDialog";

export interface EmptyShipInformationQuery {
    page: number;
    size: number;
    emptyId?: string;
    carrierId?: number;
    publisherKey?: string;
    shipKey?: string;
    portKey?: string;
    startEmptyTime?: string;
    endEmptyTime?: string;
    startReleaseTime?: string;
    endReleaseTime?: string;
    state?: number;
    origin?: number;
    existDevice?: number;
    sortKey?: string;
    sortOrder?: string;
}

export interface EmptyShipInformationPageProps {
    loadEmptyShips?: (query: EmptyShipInformationQuery) => Promise<Page<EmptyShipVo>>;
    revokeEmptyShip?: (id: number | string) => Promise<unknown>;
    permissions?: string[];
}

/** 复用 Vue 物流域空船信息分页接口，保留原查询参数命名。 */
export async function loadEmptyShipInformation(query: EmptyShipInformationQuery): Promise<Page<EmptyShipVo>> {
    const response = await shippingRequestHttp.service.get<Page<EmptyShipVo>>("/empty-ship/paging", { params: query });
    return response.data;
}

/** 复用 Vue 撤回空船信息接口。 */
export async function revokeEmptyShipInformation(id: number | string) {
    const response = await shippingRequestHttp.service.put(`/empty-ship/cancel/${id}`);
    return response.data;
}

const defaultQuery: EmptyShipInformationQuery = { page: 1, size: 10, state: EMPTY_SHIP_STATE.NORMAL.code };

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

const formatCustomer = (value?: EmptyShipVo["carrier"]) =>
    value
        ? `${value.institutionName || value.realName || DEFAULT_VALUE} | ${value.mobile || DEFAULT_VALUE} | ${value.code || DEFAULT_VALUE}`
        : DEFAULT_VALUE;

const formatShip = (row: EmptyShipVo) => {
    const ship = row.emptyShip;
    return [ship.cnname, ship.name, ship.shipId].filter(Boolean).join(" | ") || DEFAULT_VALUE;
};

const formatPorts = (ports?: Array<{ portName?: string }>) =>
    ports
        ?.map(port => port.portName)
        .filter(Boolean)
        .join("、") || DEFAULT_VALUE;

const formatPublisher = (row: EmptyShipVo) => {
    const publisher = row.emptyShip;
    const values = [publisher.publisherName, publisher.publisherMobile];
    if (Number(publisher.origin) === EMPTY_INFO_ORIGIN.SHIP_CLOUD.code) values.push(publisher.publisherCode);
    return values.filter(Boolean).join(" | ") || DEFAULT_VALUE;
};

/** 物流管理-空船信息列表，覆盖 Vue 首屏查询、分页、字段和撤回链路。 */
export function EmptyShipInformationPage({
    loadEmptyShips = loadEmptyShipInformation,
    revokeEmptyShip = revokeEmptyShipInformation,
    permissions
}: EmptyShipInformationPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 沿用 Vue v-auth：新增、修改需要处理权限，撤回需要管理权限。
    const effectivePermissions = permissions || storePermissions;
    const canDeal = effectivePermissions.includes("ROLE_EMPTY_SHIP_DEAL");
    const canManage = effectivePermissions.includes("ROLE_EMPTY_SHIP_MANAGE");
    const [rows, setRows] = useState<EmptyShipVo[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<EmptyShipInformationQuery>(defaultQuery);
    const [form, setForm] = useState<
        Pick<
            EmptyShipInformationQuery,
            | "emptyId"
            | "carrierId"
            | "publisherKey"
            | "shipKey"
            | "portKey"
            | "startEmptyTime"
            | "endEmptyTime"
            | "startReleaseTime"
            | "endReleaseTime"
            | "origin"
            | "state"
            | "existDevice"
        >
    >({});
    const [errorMessage, setErrorMessage] = useState("");
    const [carrierOptions, setCarrierOptions] = useState<Array<{ value: number; label: string }>>([]);

    useEffect(() => {
        let disposed = false;
        void loadEmptyShips(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("空船信息加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadEmptyShips, query]);

    /** 将旧 Vue 日期范围的日期值转换为后端要求的起止时间。 */
    const submitFilters = () => {
        const nextQuery: EmptyShipInformationQuery = {
            page: 1,
            size: 10,
            state: form.state ?? EMPTY_SHIP_STATE.NORMAL.code
        };
        if (form.emptyId) nextQuery.emptyId = form.emptyId;
        if (form.carrierId != null) nextQuery.carrierId = form.carrierId;
        if (form.publisherKey) nextQuery.publisherKey = form.publisherKey;
        if (form.shipKey) nextQuery.shipKey = form.shipKey;
        if (form.portKey) nextQuery.portKey = form.portKey;
        if (form.origin != null) nextQuery.origin = form.origin;
        if (form.existDevice != null) nextQuery.existDevice = form.existDevice;
        if (form.startEmptyTime) nextQuery.startEmptyTime = `${form.startEmptyTime} 00:00:00`;
        if (form.endEmptyTime) nextQuery.endEmptyTime = `${form.endEmptyTime} 23:59:59`;
        if (form.startReleaseTime) nextQuery.startReleaseTime = `${form.startReleaseTime} 00:00:00`;
        if (form.endReleaseTime) nextQuery.endReleaseTime = `${form.endReleaseTime} 23:59:59`;
        setQuery(nextQuery);
    };

    const resetFilters = () => {
        setForm({});
        setQuery(defaultQuery);
    };

    /** 对齐旧 Vue 承运商远程下拉，只在用户输入关键词时查询候选。 */
    const searchCarriers = async (value: string) => {
        if (!value.trim()) {
            setCarrierOptions([]);
            return;
        }
        try {
            const result = await import("@/api/services/customerService");
            const customers = await result.customerService.customerSelector({ param: value, personalAuth: 1 });
            setCarrierOptions(
                customers.slice(0, 50).map(customer => ({
                    value: Number(customer.id),
                    label: `${customer.institutionName || customer.realName || DEFAULT_VALUE} | ${customer.mobile || DEFAULT_VALUE} | ${customer.code || DEFAULT_VALUE}`
                }))
            );
        } catch {
            setCarrierOptions([]);
        }
    };

    const handleRevoke = useCallback(
        (id: number | string) => {
            void confirmDialog("确认撤回空船信息吗？", "提示", {
                onOk: async () => {
                    try {
                        await revokeEmptyShip(id);
                        // 保留 Vue 撤回成功提示，并通过更新查询对象刷新列表。
                        message.success("撤回成功");
                        setQuery(current => ({ ...current }));
                    } catch {
                        message.error("撤回失败");
                    }
                }
            });
        },
        [revokeEmptyShip]
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue 空船信息 ZhTable 的字段与操作插槽。
    const emptyShipColumns = useMemo<TableColumnProps<EmptyShipVo>[]>(
        () => [
            {
                key: "id",
                label: "空船信息编号",
                width: 148,
                prop: "emptyShip.id",
                render: ({ row }) => (
                    <Link to={`/logistics/emptyShipInformationDetail/${row.emptyShip.id}`}>{row.emptyShip.id}</Link>
                )
            },
            {
                key: "carrier",
                label: "承运商",
                width: 176,
                prop: "carrier",
                render: ({ row }) => formatCustomer(row.carrier)
            },
            {
                key: "ship",
                label: "船舶名称",
                width: 290,
                prop: "emptyShip.name",
                render: ({ row }) => formatShip(row)
            },
            {
                key: "tonCapacity",
                label: "实装吨位",
                width: 197,
                prop: "emptyShip.tonCapacity",
                render: ({ row }) => row.emptyShip.tonCapacity ?? DEFAULT_VALUE
            },
            {
                key: "originPorts",
                label: "空船始发地",
                width: 140,
                prop: "originPorts",
                render: ({ row }) => formatPorts(row.originPorts)
            },
            {
                key: "destPorts",
                label: "目的地",
                minWidth: 195,
                prop: "destPorts",
                render: ({ row }) => formatPorts(row.destPorts)
            },
            {
                key: "emptyTime",
                label: "预计空船时间",
                width: 210,
                prop: "emptyShip.emptyStartTime",
                render: ({ row }) =>
                    `${formatDate(row.emptyShip.emptyStartTime)} 至 ${formatDate(row.emptyShip.emptyEndTime)}`
            },
            {
                key: "origin",
                label: "信息来源",
                width: 170,
                prop: "emptyShip.origin",
                render: ({ row }) => EMPTY_INFO_ORIGIN.getIntroByCode(row.emptyShip.origin) || DEFAULT_VALUE
            },
            {
                key: "publisher",
                label: "发布人",
                width: 200,
                prop: "publisher",
                render: ({ row }) => formatPublisher(row)
            },
            {
                key: "publishTime",
                label: "发布时间",
                width: 150,
                prop: "emptyShip.publishTime",
                sortable: true,
                render: ({ row }) => formatDate(row.emptyShip.publishTime)
            },
            {
                key: "state",
                label: "状态",
                width: 120,
                prop: "emptyShip.state",
                render: ({ row }) => <StatusTag>{EMPTY_SHIP_STATE.getIntroByCode(row.emptyShip.state) || DEFAULT_VALUE}</StatusTag>
            },
            {
                key: "existDevice",
                label: "摄像头",
                width: 120,
                prop: "emptyShip.existDevice",
                render: ({ row }) =>
                    EXIST_DEVICE.getIntroByCode(
                        (row.emptyShip as EmptyShipVo["emptyShip"] & { existDevice?: number }).existDevice
                    ) || DEFAULT_VALUE
            },
            {
                key: "operation",
                label: "操作",
                width: 240,
                prop: "operation",
                fixed: "right",
                render: ({ row }) =>
                    Number(row.emptyShip.state) === EMPTY_SHIP_STATE.NORMAL.code ? (
                        <Space>
                            {canDeal ? (
                                <Link to={`/logistics/emptyShipInformationEdit/${row.emptyShip.id}`}>
                                    <Button disabled={banButton} type="link">
                                        修改
                                    </Button>
                                </Link>
                            ) : null}
                            {canManage ? (
                                <Button disabled={banButton} type="link" onClick={() => handleRevoke(row.emptyShip.id)}>
                                    撤回
                                </Button>
                            ) : null}
                        </Space>
                    ) : null
            }
        ],
        [banButton, canDeal, canManage, handleRevoke]
    );

    // 保留 Vue 旧表格对发布时间的 sortKey/sortOrder 请求契约。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "emptyShip.publishTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "created_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    return (
        <section aria-label="empty-ship-information-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<EmptyShipVo>
                    rowKey={row => row.emptyShip.id}
                    data={rows}
                    columns={normalizeOperationColumns(emptyShipColumns, rows)}
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Space className="mb-4 w-full" align="center">
                                <Typography.Title className="mb-0 mt-0" level={4}>
                                    空船信息
                                </Typography.Title>
                                {canDeal ? (
                                    <Link to="/logistics/emptyShipInformationAdd">
                                        <Button disabled={banButton} type="primary">
                                            新增
                                        </Button>
                                    </Link>
                                ) : null}
                            </Space>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form className="admin-table-filter-form mb-4" layout="inline" onFinish={submitFilters}>
                                <Form.Item label="空船信息编号">
                                    <Input
                                        aria-label="空船信息编号"
                                        maxLength={32}
                                        placeholder="请输入空船信息编号搜索"
                                        value={form.emptyId || ""}
                                        onChange={event => setForm({ ...form, emptyId: event.target.value })}
                                    />
                                </Form.Item>
                                <Form.Item label="承运商">
                                    <Select
                                        aria-label="承运商"
                                        filterOption={false}
                                        options={carrierOptions}
                                        placeholder="请选择承运商"
                                        showSearch
                                        value={form.carrierId}
                                        onSearch={value => void searchCarriers(value)}
                                        onChange={value =>
                                            setForm({ ...form, carrierId: value ? Number(value) : undefined })
                                        }
                                    />
                                </Form.Item>
                                <Form.Item label="船舶名称">
                                    <Input
                                        aria-label="船舶名称"
                                        placeholder="请输入船舶名关键词或MMSI搜索"
                                        value={form.shipKey || ""}
                                        onChange={event => setForm({ ...form, shipKey: event.target.value })}
                                    />
                                </Form.Item>
                                <Form.Item label="码头城市">
                                    <Input
                                        aria-label="码头城市"
                                        placeholder="请输入城市/码头关键词搜索"
                                        value={form.portKey || ""}
                                        onChange={event => setForm({ ...form, portKey: event.target.value })}
                                    />
                                </Form.Item>
                                <Form.Item label="发布人">
                                    <Input
                                        aria-label="发布人"
                                        placeholder="请输入发布人信息搜索"
                                        value={form.publisherKey || ""}
                                        onChange={event => setForm({ ...form, publisherKey: event.target.value })}
                                    />
                                </Form.Item>
                                <Form.Item label="预计空船时间">
                                    <ZhDateRangePicker
                                        rangeSeparator="-"
                                        type="daterange"
                                        valueFormat="YYYY-MM-DD"
                                        startPlaceholder="空船开始时间"
                                        endPlaceholder="空船结束时间"
                                        value={[form.startEmptyTime || null, form.endEmptyTime || null]}
                                        onChange={(_value, formattedValue) =>
                                            setForm({
                                                ...form,
                                                startEmptyTime: formattedValue?.[0] || undefined,
                                                endEmptyTime: formattedValue?.[1] || undefined
                                            })
                                        }
                                    />
                                </Form.Item>
                                <Form.Item label="发布时间">
                                    <ZhDateRangePicker
                                        rangeSeparator="-"
                                        type="daterange"
                                        valueFormat="YYYY-MM-DD"
                                        startPlaceholder="发布开始时间"
                                        endPlaceholder="发布结束时间"
                                        value={[form.startReleaseTime || null, form.endReleaseTime || null]}
                                        onChange={(_value, formattedValue) =>
                                            setForm({
                                                ...form,
                                                startReleaseTime: formattedValue?.[0] || undefined,
                                                endReleaseTime: formattedValue?.[1] || undefined
                                            })
                                        }
                                    />
                                </Form.Item>
                                <Form.Item label="信息来源">
                                    <Select
                                        aria-label="信息来源"
                                        allowClear
                                        placeholder="请选择信息来源"
                                        options={EMPTY_INFO_ORIGIN.options.map(option => ({
                                            value: Number(option.value),
                                            label: option.label
                                        }))}
                                        value={form.origin}
                                        onChange={value =>
                                            setForm({ ...form, origin: value == null ? undefined : Number(value) })
                                        }
                                    />
                                </Form.Item>
                                <Form.Item label="状态">
                                    <Select
                                        aria-label="状态"
                                        allowClear
                                        placeholder="请选择状态"
                                        options={EMPTY_SHIP_STATE.options.map(option => ({
                                            value: Number(option.value),
                                            label: option.label
                                        }))}
                                        value={form.state}
                                        onChange={value =>
                                            setForm({ ...form, state: value == null ? undefined : Number(value) })
                                        }
                                    />
                                </Form.Item>
                                <Form.Item label="摄像头">
                                    <Select
                                        aria-label="摄像头"
                                        allowClear
                                        placeholder="请选择摄像头状态"
                                        options={EXIST_DEVICE.options.map(option => ({
                                            value: Number(option.value),
                                            label: option.label
                                        }))}
                                        value={form.existDevice}
                                        onChange={value =>
                                            setForm({ ...form, existDevice: value == null ? undefined : Number(value) })
                                        }
                                    />
                                </Form.Item>
                                <Space>
                                    <Button aria-label="查询" htmlType="submit" type="primary">
                                        查询
                                    </Button>
                                    <Button onClick={resetFilters}>重置</Button>
                                </Space>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                total={total}
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    // 切换每页条数时回到第一页，保持旧 Vue ProTable 的分页行为。
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}
